import React, { useState, useEffect } from "react";
import { Drawer, Select, Input, Typography, Row, Col, Spin, Icon, Radio } from "antd";
import { getContactEmails } from "../../api/Billing";
import { alertMessage } from "../../common/Common";
import {
  CancelIcon,
  CircledCloseIcon,
  SentIcon,
} from "../../common/CustomIcon";
import AddContactByButton from "../../components/configurations/Contacts/AddContactByButton";
import Button_v2 from "./Button_v2";
import { isValidEmail } from "../../components/billing_screen/helpers";
import { renderNotification } from "../../components/orders/OrderTypeIcons";
import I18n from "../../common/I18n";

const PODEmailDrawer = ({
  description,
  title = 'POD Email Form',
  currentOrder,
  accountId,
  isPODemailDrawerOpen,
  setIsPODemailDrawerOpen,
  onSendToEmail,
  isClosable = false,
  isEmailLoading,
  showOnHandReciptMenu,
  isArrivalNotice = false,
  onDownloadArrivalNotice,
  isDownloadLoading = false,
  onPreviewArrivalNotice,
  isPreviewLoading = false,
}) => {
  const emailObj = {
    emails: [],
    description: isArrivalNotice ? "Please find the attached PDF." : description,
    special_instruction: "",
    file_attachment: "ORDER",
  };

  const [PODemailData, setPODemailData] = useState(emailObj);
  const [contactEmails, setContactEmails] = useState([]);

  useEffect(() => {
    // Only load account emails when the drawer is actually opened.
    // Mounting with a new currentOrder reference (common in parents) must not
    // trigger get_account_emails on select/preview.
    if (!isPODemailDrawerOpen || !accountId) {
      return;
    }

    const fetchEmails = async () => {
      try {
        const result = await getContactEmails(accountId);
        if (result?.success) {
          setContactEmails(result?.contact_emails);
        } else {
          alertMessage(result.errors[0], "error", 10);
        }
      } catch (error) {
        console.error("Error fetching emails:", error);
        alertMessage(
          "An unexpected error occurred while fetching emails.",
          "error",
          10
        );
      }
    };

    fetchEmails();
  }, [accountId, isPODemailDrawerOpen]);

  const handleEmailSelect = (email) => {
    if (!isValidEmail(email)) {
      renderNotification(`"${email}" is not a valid email address.`, 'error');
    } else {
      setPODemailData((prevData) => {
        if (!prevData.emails.includes(email)) {
          return { ...prevData, emails: [...prevData.emails, email] };
        }
        return prevData;
      });
    }
  };

  const handleEmailDeselect = (email) => {
    setPODemailData((prevData) => ({
      ...prevData,
      emails: prevData.emails.filter((e) => e !== email),
    }));
  };

  const handleDescriptionChange = (e) => {
    setPODemailData({ ...PODemailData, description: e.target.value });
  };

  const handleSpecialInstructionChange = (e) => {
    setPODemailData({ ...PODemailData, special_instruction: e.target.value });
  };

  const handleContactSaved = (contact) => {
    if (contact?.email) {
      setContactEmails((prev) => [...prev, contact.email]);
    }
  };

  return (
    <Drawer
      className="custom-drawer-docs"
      placement="right"
      width="40%"
      closable={false}
      visible={isPODemailDrawerOpen}
      onClose={() => {
        setPODemailData(emailObj);
        setIsPODemailDrawerOpen(false);
      }}
      destroyOnClose={true}
      headerStyle={{ border: "none" }}
      bodyStyle={{
        marginTop: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        justifyContent: "center",
      }}
      title={
        <div
          data-testid="pod-email-modal"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Row type="flex">
            <Col>
              <Typography.Title
                level={2}
                style={{
                  fontFamily: "Lato",
                  fontSize: "32px",
                  fontWeight: 600,
                  color: "#111314",
                  marginBottom: '0px'
                }}
              >
                {title}
              </Typography.Title>
            </Col>
          </Row>
          {isClosable && (
            <Row>
              <Col>
                <CircledCloseIcon
                  color="#576EC1"
                  width="54px"
                  height="54px"
                  onClick={() => {
                    setPODemailData(emailObj);
                    setIsPODemailDrawerOpen(false);
                  }}
                  style={{ cursor: "pointer" }}
                />
              </Col>
            </Row>
          )}
        </div>
      }
    >
      <Select
        data-testid="add-email-dropdown"
        style={{ width: "100%" }}
        mode="tags"
        value={PODemailData.emails}
        onSelect={handleEmailSelect}
        onDeselect={handleEmailDeselect}
        showSearch
        filterOption={(input, option) =>
          option.props.children
            .toLowerCase()
            .indexOf(input.toLowerCase()) >= 0
        }
        tokenSeparators={[",", " "]}
        placeholder="Add Email"
      >
        {contactEmails?.map((email) => (
          <Select.Option key={email} value={email}>
            {email}
          </Select.Option>
        ))}
      </Select>

      <div style={{ display: "flex", justifyContent: "flex-end" }} data-testid="add-email-button">
        <AddContactByButton
          zIndex={1002}
          accountId={accountId}
          saveOn={handleContactSaved}
          size="medium"
        />
      </div>

      {isArrivalNotice ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Typography.Text strong style={{ fontSize: "14px" }}>
            Special Instruction
          </Typography.Text>
          <Input.TextArea
            data-testid="special-instruction-input"
            placeholder="Enter Special Instruction"
            value={PODemailData.special_instruction}
            onChange={handleSpecialInstructionChange}
            rows={4}
            autosize={{ minRows: 4, maxRows: 10 }}
            style={{ resize: "vertical" }}
          />
        </div>
      ) : (
        <Input.TextArea
          data-testid="description-input"
          placeholder="Enter Description"
          value={PODemailData.description}
          onChange={handleDescriptionChange}
          rows={4}
          autosize={{ minRows: 4, maxRows: 10 }}
          style={{ resize: "vertical" }}
        />
      )}

      {isArrivalNotice ? null : (
        <div data-testid="order-email-select" style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "8px" }}>
          <Typography.Text strong style={{ fontSize: "14px" }}>
            {I18n.t('general.file_attachment')} :
          </Typography.Text>

          <Radio.Group
            value={PODemailData.file_attachment || "ORDER"}
            onChange={(e) =>
              setPODemailData({ ...PODemailData, file_attachment: e.target.value })
            }
          >
            <Radio value="ORDER">{I18n.t("order.order_details_pdf_lable")}</Radio>
            {showOnHandReciptMenu && <>
            <Radio value="ON_HAND">{I18n.t("order.onHand_receipt_lable")}</Radio>
            <Radio value="BOTH">{I18n.t("general.both")}</Radio>
            </>}
          </Radio.Group>
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <Button_v2
          data-testid="cancel-email-button"
          style={{ marginRight: "8px" }}
          className="drawer-cancel-button"
          text={
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <CancelIcon
                styling={{ width: "20px", height: "20px" }}
                color="#D23B71"
                style={{ cursor: "pointer" }}
              />
              Cancel
            </div>
          }
          onClick={() => {
            setPODemailData(emailObj);
            setIsPODemailDrawerOpen(false);
          }}
        />
        {isArrivalNotice && onPreviewArrivalNotice && (
          <Button_v2
            data-testid="preview-arrival-notice-button"
            style={{ marginRight: "8px" }}
            className="drawer-send-button"
            text={
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {isPreviewLoading ? (
                  <Spin indicator={<Icon type="loading" spin style={{ fontSize: 16 }} />} size="small" />
                ) : (
                  <Icon type="eye" style={{ fontSize: 16, color: "#576EC1" }} />
                )}
                Preview
              </div>
            }
            onClick={() => {
              if (!isPreviewLoading && !isDownloadLoading && !isEmailLoading) {
                onPreviewArrivalNotice(PODemailData);
              }
            }}
            disabled={isPreviewLoading || isDownloadLoading || isEmailLoading}
          />
        )}
        {isArrivalNotice && onDownloadArrivalNotice && (
          <Button_v2
            data-testid="download-arrival-notice-button"
            style={{ marginRight: "8px" }}
            className="drawer-send-button"
            text={
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {isDownloadLoading ? (
                  <Spin indicator={<Icon type="loading" spin style={{ fontSize: 16 }} />} size="small" />
                ) : (
                  <Icon type="download" style={{ fontSize: 16, color: "#576EC1" }} />
                )}
                Download
              </div>
            }
            onClick={() => {
              if (!isDownloadLoading && !isEmailLoading && !isPreviewLoading) {
                onDownloadArrivalNotice(PODemailData);
              }
            }}
            disabled={isDownloadLoading || isEmailLoading || isPreviewLoading}
          />
        )}
        <Button_v2
          data-testid="send-email-button"
          className="drawer-send-button"
          text={
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {isEmailLoading ? (
                <Spin indicator={<Icon type="loading" spin style={{ fontSize: 16 }} />} size="small" />
              ) : (
                <SentIcon
                  styling={{ width: "20px", height: "20px" }}
                  color="#576EC1"
                  style={{ cursor: "pointer" }}
                />
              )}
              Send Email
            </div>
          }
          onClick={() => {
            if (!isEmailLoading) {
              onSendToEmail(PODemailData);
            }
          }}
          disabled={PODemailData.emails.length === 0}
        />
      </div>
    </Drawer>
  );
};

export default PODEmailDrawer;
